import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { loadFromSupabase, saveToSupabase } from "../supabaseDB";
import { SL_ISOTIPO_B64 } from "./brandLogos";

/* SweetLeaf settings page — singleton row in sweetleaf_settings.
   Tracks the invoice consecutive counter + prefix, the dedicated
   Gmail account, and the HTML signature appended to outbound mail.
   Reached via the Settings nav item (initialTab="settings").
   Persists to Supabase + the gfi-sl-settings LS cache. */

var DEFAULT_FACTORING_EMAIL_TEMPLATE = "Anexo facturas para esta semana, favor de enviar correo a Ben Wright benw@fsproduce.com y copiar a Brian\n\nBen, can you please confirm you have received the following loads under the status mentioned?, documents attached.";

var DEFAULT_FACTORING_EMAIL_SUBJECT = "Received Confirmation Request";

/* Default disclosure appended to every SweetLeaf outbound email, below
   the signature (see appendSlDisclosure() in SweetLeafPortal.js). Kept
   in sync manually with that file's own SL_DEFAULT_DISCLOSURE fallback
   — this file isn't imported there, so the two copies aren't unified
   into one shared constant. */
var DEFAULT_CONFIDENTIALITY_DISCLOSURE = "CONFIDENTIALITY NOTICE\nThe information contained in this e-mail is confidential and/or proprietary to Sweetleaf Produce Inc. and/or its affiliates and may only be used solely in performance of work or services Sweetleaf Produce Inc. The information transmitted herewith is intended only for use by the individual or entity to which it is addressed. If the reader of this message is not the intended recipient, you are hereby notified that any review, retransmission, dissemination, distribution, copying or other use of, or taking of any action in reliance upon this information is strictly prohibited. If you have received this communication in error, please contact the sender and delete the material from your computer.";

var SL_DEFAULT_SETTINGS = {
  id: "sl",
  nextInvoiceNumber: 16229,
  invoicePrefix: "INV-",
  emailAccount: "a.gonzalez@sweetleafproduce.com",
  maskedEmailAccount: "a.gonzalez.sweetleaf@gfinexus.com",
  emailSignature: "",
  /* 2026-09-24 (Arturo, confirmed via screenshot): the old hardcoded
     default pair ("Ben Wright"/"Brian Arena" @ fsproduce.com) were
     real Four Seasons Produce (the CLIENT) contacts, NOT AgroCap — a
     device whose local settings cache never picked up the real
     AgroCap contacts (e.g. a phone that had never opened this screen)
     silently fell back to mailing the client instead of the factoring
     company. Removed — starts empty until Arturo configures the real
     AgroCap contacts here, same pattern already used by
     passingRecipients below. */
  factoringRecipients: [],
  factoringEmailSubject: DEFAULT_FACTORING_EMAIL_SUBJECT,
  factoringEmailTemplate: DEFAULT_FACTORING_EMAIL_TEMPLATE,
  /* Contacts who receive the Passing/BOL email (EmailPassingModal in
     SweetLeafPortal.js). No hardcoded default pair like Factoring's
     Ben/Brian — starts empty until Arturo configures it here. */
  passingRecipients: [],
  pastDueRecipients: [],
  /* Editable disclosure appended to the end of every SweetLeaf email,
     below the signature. See PARTE 1/2 of fix-dashboard.md — this
     used to be hardcoded HTML inside EmailPassingModal only; now it's
     one setting shared by every SL send flow. */
  confidentialityDisclosure: DEFAULT_CONFIDENTIALITY_DISCLOSURE,
  /* Automated Protection Status Reminder (2026-09-23) — configurable
     interval + hard cap, read by netlify/functions/protection-reminder-
     send.js's daily cron. Kept in sync with SweetLeafPortal.js's own
     SL_DEFAULT_SETTINGS copy (that file isn't imported here, same
     duplication precedent as DEFAULT_CONFIDENTIALITY_DISCLOSURE above). */
  protectionReminderIntervalDays: 5,
  protectionReminderMaxAuto: 3,
  /* 2026-09-29 (Arturo: "SL Automated") — blank by default, falls
     back to maskedEmailAccount (see protection-reminder-send.js). */
  protectionReminderMaskedEmailAccount: "",
};

var T = {
  en: {
    title: "SweetLeaf Settings",
    subtitle: "Consecutive, email account, and signature used by the module.",
    nextNum: "Next Invoice Number",
    nextNumHelp: "The next invoice will be generated with this number (5 digits, padded).",
    prefix: "Invoice Prefix",
    prefixHelp: "Concatenated before the number (e.g. INV- + 16229 -> INV-16229).",
    emailAccount: "Email Account",
    emailAccountHelp: "Gmail account SweetLeaf sends mail from.",
    maskedEmailAccount: "Masked From (gfinexus.com)",
    maskedEmailAccountHelp: "Alias @gfinexus.com used as actual sender. Required by Resend domain verification.",
    emailSignature: "HTML Signature",
    emailSignatureHelp: "Appended to outbound emails from SweetLeaf.",
    upload: "Upload .html",
    uploadErr: "Could not read file",
    preview: "Preview",
    save: "Save",
    saved: "Settings saved",
    factoringRecipients: "Factoring Recipients",
    factoringRecipientsHelp: "Contacts who receive the weekly factoring email to AGROCAP.",
    addRecipient: "+ Add contact",
    removeRecipient: "Remove",
    factoringSubject: "Factoring Email Subject",
    factoringSubjectHelp: "Subject line used when sending the weekly factoring email.",
    factoringTemplate: "Factoring Email Template",
    factoringTemplateHelp: "Introductory text for the weekly factoring email. The invoice table and note are added automatically below.",
    passingRecipients: "Passing Recipients",
    passingRecipientsHelp: "Contacts who receive the Passing/BOL email.",
    pastDueRecipients: "Past Due Notice Recipients",
    pastDueRecipientsHelp: "Contacts who receive the past due invoice notice.",
    confidentialityDisclosure: "Email Confidentiality Notice",
    confidentialityDisclosureHelp: "This text is added automatically at the end of every email sent from SweetLeaf, below your signature. Edit here if the legal disclosure changes.",
    contactName: "Name",
    contactEmail: "Email",
    protectionReminderTitle: "Automated Protection Status Reminders",
    protectionReminderIntervalDays: "Reminder Interval (days)",
    protectionReminderIntervalDaysHelp: "How often an automatic reminder repeats while an invoice stays under Protection/PAS with no final response (Full Pay / Credit).",
    protectionReminderMaxAuto: "Max Automatic Reminders",
    protectionReminderMaxAutoHelp: "The cron stops sending on an invoice after this many automatic reminders. You can still send more manually from the invoice's kebab menu — this cap only applies to the automated job.",
    protectionReminderMaskedEmailAccount: "Automated Sender (SL Automated)",
    protectionReminderMaskedEmailAccountHelp: "Dedicated, name-free @gfinexus.com alias used ONLY by the automated daily reminder job. Leave blank to use the Masked From above instead. Every manual SweetLeaf send is unaffected by this field.",
  },
  es: {
    title: "Configuracion SweetLeaf",
    subtitle: "Consecutivo, cuenta de email y firma usados por el modulo.",
    nextNum: "Proximo Numero de Factura",
    nextNumHelp: "El proximo invoice se generara con este numero (5 digitos, con padding).",
    prefix: "Prefijo del Invoice",
    prefixHelp: "Se concatena antes del numero (ej. INV- + 16229 -> INV-16229).",
    emailAccount: "Cuenta de Email",
    emailAccountHelp: "Cuenta Gmail desde la que SweetLeaf envia correos.",
    maskedEmailAccount: "From Enmascarado (gfinexus.com)",
    maskedEmailAccountHelp: "Alias @gfinexus.com que se usa como remitente real. Requerido por verificación de dominio en Resend.",
    emailSignature: "Firma HTML",
    emailSignatureHelp: "Se anexa al final de los correos enviados desde SweetLeaf.",
    upload: "Subir .html",
    uploadErr: "No se pudo leer el archivo",
    preview: "Vista previa",
    save: "Guardar",
    saved: "Configuracion guardada",
    factoringRecipients: "Destinatarios Factoring",
    factoringRecipientsHelp: "Contactos que reciben el email semanal de factoraje a AGROCAP.",
    addRecipient: "+ Agregar contacto",
    removeRecipient: "Eliminar",
    factoringSubject: "Asunto Email Factoring",
    factoringSubjectHelp: "Línea de asunto usada al enviar el email semanal de factoraje.",
    factoringTemplate: "Plantilla Email Factoring",
    factoringTemplateHelp: "Texto introductorio del email semanal de factoraje. La tabla de facturas y la nota se agregan automáticamente debajo.",
    passingRecipients: "Destinatarios de Passing",
    passingRecipientsHelp: "Contactos que reciben el email de Passing/BOL.",
    pastDueRecipients: "Destinatarios de Aviso de Vencidas",
    pastDueRecipientsHelp: "Contactos que reciben el aviso de facturas vencidas.",
    confidentialityDisclosure: "Aviso de Confidencialidad de Email",
    confidentialityDisclosureHelp: "Este texto se agrega automáticamente al final de cada email enviado desde SweetLeaf, debajo de tu firma. Edita aquí si cambia el disclaimer legal.",
    contactName: "Nombre",
    contactEmail: "Email",
    protectionReminderTitle: "Recordatorios Automáticos de Estatus — Protección",
    protectionReminderIntervalDays: "Intervalo de Recordatorio (días)",
    protectionReminderIntervalDaysHelp: "Cada cuántos días se repite un recordatorio automático mientras la factura siga en Protección/PAS sin una respuesta final (Pago Completo / Crédito).",
    protectionReminderMaxAuto: "Máximo de Recordatorios Automáticos",
    protectionReminderMaxAutoHelp: "El cron deja de enviar a una factura después de este número de recordatorios automáticos. Sigues pudiendo enviar más manualmente desde el menú (⋮) de la factura — este límite solo aplica al job automático.",
    protectionReminderMaskedEmailAccount: "Remitente Automático (SL Automated)",
    protectionReminderMaskedEmailAccountHelp: "Alias @gfinexus.com dedicado, sin nombre, usado SOLO por el job automático diario de recordatorios. Déjalo vacío para usar el From Enmascarado de arriba. Ningún envío manual de SweetLeaf se ve afectado por este campo.",
  },
};

var inp = { padding: "8px 10px", fontSize: 13, border: "1px solid var(--color-border)", borderRadius: 6, background: "var(--color-bg-input)", color: "var(--color-text)", outline: "none", fontFamily: "inherit", width: "100%", boxSizing: "border-box" };

export default function SweetLeafSettingsPanel({ lang }) {
  var l = T[lang === "es" ? "es" : "en"];
  var [form, setForm] = useState(function () {
    try {
      var raw = localStorage.getItem("gfi-sl-settings");
      if (raw) {
        var parsed = JSON.parse(raw);
        return Object.assign({}, SL_DEFAULT_SETTINGS, parsed);
      }
    } catch (e) {}
    return Object.assign({}, SL_DEFAULT_SETTINGS);
  });
  var [dirty, setDirty] = useState(false);
  var [saving, setSaving] = useState(false);
  var [toast, setToast] = useState(null);

  useEffect(function () {
    var alive = true;
    loadFromSupabase("sweetleaf_settings")
      .then(function (rows) {
        if (!alive) return;
        if (rows && rows.length > 0) {
          var rec = rows[0];
          setForm(function (prev) { return Object.assign({}, SL_DEFAULT_SETTINGS, prev, rec); });
        }
      })
      .catch(function (e) { if (typeof console !== "undefined" && console.warn) console.warn("[SweetLeafSettings] load failed:", e && e.message); });
    return function () { alive = false; };
  }, []);

  function set(k, v) { setForm(function (prev) { var n = Object.assign({}, prev); n[k] = v; return n; }); setDirty(true); }

  async function doSave() {
    if (saving) return;
    setSaving(true);
    try {
      var payload = Object.assign({ id: "sl" }, form);
      await saveToSupabase("sweetleaf_settings", payload);
      try { localStorage.setItem("gfi-sl-settings", JSON.stringify(form)); } catch (e) {}
      setDirty(false);
      setToast(l.saved);
      setTimeout(function () { setToast(null); }, 2200);
    } catch (e) {
      alert("Error: " + (e.message || String(e)));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ maxWidth: 720, paddingRight: 4, maxHeight: "calc(100vh - 120px)", overflowY: "auto" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <img src={SL_ISOTIPO_B64} alt="SweetLeaf" style={{ width: 36, height: 36, objectFit: "contain" }} />
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0, color: "#2A7A6F" }}>{l.title}</h2>
          <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginTop: 2 }}>{l.subtitle}</div>
        </div>
      </div>

      <div style={{ background: "var(--color-bg-card)", border: "1px solid var(--color-border)", borderRadius: 10, padding: 20 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 18 }}>
          <div>
            <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>{l.nextNum}</label>
            <input
              type="number"
              min="1"
              value={form.nextInvoiceNumber || ""}
              onChange={function (e) { set("nextInvoiceNumber", parseInt(e.target.value, 10) || 0); }}
              style={Object.assign({}, inp, { fontFamily: "'JetBrains Mono', monospace", fontWeight: 700 })}
            />
            <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 3, fontStyle: "italic" }}>{l.nextNumHelp}</div>
          </div>
          <div>
            <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>{l.prefix}</label>
            <input
              type="text"
              value={form.invoicePrefix || ""}
              onChange={function (e) { set("invoicePrefix", e.target.value); }}
              style={Object.assign({}, inp, { fontFamily: "'JetBrains Mono', monospace", fontWeight: 700 })}
            />
            <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 3, fontStyle: "italic" }}>{l.prefixHelp}</div>
          </div>
        </div>

        <div style={{ marginBottom: 18 }}>
          <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>{l.emailAccount}</label>
          <input
            type="email"
            value={form.emailAccount || ""}
            onChange={function (e) { set("emailAccount", e.target.value); }}
            style={Object.assign({}, inp, { fontFamily: "'JetBrains Mono', monospace" })}
            placeholder="a.gonzalez@sweetleafproduce.com"
          />
          <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 3, fontStyle: "italic" }}>{l.emailAccountHelp}</div>
        </div>

        <div style={{ marginBottom: 18 }}>
          <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>{l.maskedEmailAccount}</label>
          <input
            type="email"
            value={form.maskedEmailAccount || ""}
            onChange={function (e) { set("maskedEmailAccount", e.target.value); }}
            style={Object.assign({}, inp, { fontFamily: "'JetBrains Mono', monospace" })}
            placeholder="a.gonzalez.sweetleaf@gfinexus.com"
          />
          <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 3, fontStyle: "italic" }}>{l.maskedEmailAccountHelp}</div>
        </div>

        <div style={{ marginBottom: 18 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em" }}>{l.emailSignature}</label>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 10px", borderRadius: 5, border: "1px dashed var(--color-border)", background: "transparent", color: "#2A7A6F", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>
              ⬆ {l.upload}
              <input
                type="file"
                accept=".html,.htm,text/html"
                style={{ display: "none" }}
                onChange={function (e) {
                  var f = e.target.files && e.target.files[0]; if (!f) return;
                  var r = new FileReader();
                  r.onload = function (ev) { set("emailSignature", String(ev.target.result || "")); };
                  r.onerror = function () { alert(l.uploadErr); };
                  r.readAsText(f);
                  e.target.value = "";
                }}
              />
            </label>
          </div>
          <textarea
            value={form.emailSignature || ""}
            onChange={function (e) { set("emailSignature", e.target.value); }}
            rows={6}
            style={Object.assign({}, inp, { resize: "vertical", fontFamily: "'JetBrains Mono', monospace", fontSize: 11 })}
            placeholder="<div>...</div>"
          />
          <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 3, fontStyle: "italic" }}>{l.emailSignatureHelp}</div>
          {form.emailSignature ? (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 700 }}>{l.preview}</div>
              <div style={{ padding: 10, borderRadius: 6, border: "1px dashed var(--color-border)", background: "#ffffff", color: "#1a1a1a" }} dangerouslySetInnerHTML={{ __html: form.emailSignature }} />
            </div>
          ) : null}
        </div>

        {/* Factoring Recipients — list of contacts who receive the
            weekly AGROCAP factoring email. Mirrors the client
            distribution-list shape ({ id, name, email }). */}
        <div style={{ marginBottom: 18 }}>
          <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>{l.factoringRecipients}</label>
          <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 8, fontStyle: "italic" }}>{l.factoringRecipientsHelp}</div>
          {(form.factoringRecipients || []).length === 0 && (
            <div style={{ fontSize: 11, color: "var(--color-text-muted)", padding: "8px 4px", fontStyle: "italic" }}>—</div>
          )}
          {(form.factoringRecipients || []).map(function (r, idx) {
            return (
              <div key={r.id || idx} style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr auto", gap: 8, marginBottom: 8 }}>
                <input
                  type="text"
                  placeholder={l.contactName}
                  value={r.name || ""}
                  onChange={function (e) {
                    var v = e.target.value;
                    set("factoringRecipients", (form.factoringRecipients || []).map(function (rr, i) { return i === idx ? Object.assign({}, rr, { name: v }) : rr; }));
                  }}
                  style={Object.assign({}, inp, { fontWeight: 600 })}
                />
                <input
                  type="email"
                  placeholder={l.contactEmail}
                  value={r.email || ""}
                  onChange={function (e) {
                    var v = e.target.value;
                    set("factoringRecipients", (form.factoringRecipients || []).map(function (rr, i) { return i === idx ? Object.assign({}, rr, { email: v }) : rr; }));
                  }}
                  style={Object.assign({}, inp, { fontFamily: "'JetBrains Mono', monospace" })}
                />
                <button
                  type="button"
                  onClick={function () {
                    set("factoringRecipients", (form.factoringRecipients || []).filter(function (_, i) { return i !== idx; }));
                  }}
                  style={{ padding: "7px 12px", borderRadius: 6, border: "1px solid rgba(231,76,60,0.3)", background: "rgba(231,76,60,0.06)", color: "var(--color-danger, #DC2626)", cursor: "pointer", fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" }}
                >× {l.removeRecipient}</button>
              </div>
            );
          })}
          <button
            type="button"
            onClick={function () {
              var list = form.factoringRecipients || [];
              set("factoringRecipients", list.concat([{ id: Date.now() + Math.random(), name: "", email: "" }]));
            }}
            style={{ padding: "8px 14px", borderRadius: 6, border: "1px dashed var(--color-border)", background: "transparent", color: "#2A7A6F", cursor: "pointer", fontSize: 12, fontWeight: 600, width: "100%", marginTop: 4 }}
          >{l.addRecipient}</button>
        </div>

        {/* Passing Recipients — contacts who receive the Passing/BOL
            email (EmailPassingModal in SweetLeafPortal.js). Same
            { id, name, email } row shape and editing UI as Factoring
            Recipients above — deliberately duplicated markup rather
            than a shared sub-component, per this file's own
            Factoring-block precedent. */}
        <div style={{ marginBottom: 18 }}>
          <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>{l.pastDueRecipients}</label>
          <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 8, fontStyle: "italic" }}>{l.pastDueRecipientsHelp}</div>
          {(form.pastDueRecipients || []).length === 0 && (
            <div style={{ fontSize: 11, color: "var(--color-text-muted)", padding: "8px 4px", fontStyle: "italic" }}>—</div>
          )}
          {(form.pastDueRecipients || []).map(function (r, idx) {
            return (
              <div key={r.id || idx} style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr auto", gap: 8, marginBottom: 8 }}>
                <input type="text" placeholder={l.contactName} value={r.name || ""} onChange={function (e) { var v = e.target.value; set("pastDueRecipients", (form.pastDueRecipients || []).map(function (rr, i) { return i === idx ? Object.assign({}, rr, { name: v }) : rr; })); }} style={Object.assign({}, inp, { fontWeight: 600 })} />
                <input type="email" placeholder={l.contactEmail} value={r.email || ""} onChange={function (e) { var v = e.target.value; set("pastDueRecipients", (form.pastDueRecipients || []).map(function (rr, i) { return i === idx ? Object.assign({}, rr, { email: v }) : rr; })); }} style={Object.assign({}, inp, { fontFamily: "'JetBrains Mono', monospace" })} />
                <button type="button" onClick={function () { set("pastDueRecipients", (form.pastDueRecipients || []).filter(function (_, i) { return i !== idx; })); }} style={{ padding: "7px 12px", borderRadius: 6, border: "1px solid rgba(231,76,60,0.3)", background: "rgba(231,76,60,0.06)", color: "var(--color-danger, #DC2626)", cursor: "pointer", fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" }}>× {l.removeRecipient}</button>
              </div>
            );
          })}
          <button type="button" onClick={function () { set("pastDueRecipients", (form.pastDueRecipients || []).concat([{ id: Date.now() + Math.random(), name: "", email: "" }])); }} style={{ padding: "8px 14px", borderRadius: 6, border: "1px dashed var(--color-border)", background: "transparent", color: "#2A7A6F", cursor: "pointer", fontSize: 12, fontWeight: 600, width: "100%", marginTop: 4 }}>{l.addRecipient}</button>
        </div>
        <div style={{ marginBottom: 18 }}>
          <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>{l.passingRecipients}</label>
          <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 8, fontStyle: "italic" }}>{l.passingRecipientsHelp}</div>
          {(form.passingRecipients || []).length === 0 && (
            <div style={{ fontSize: 11, color: "var(--color-text-muted)", padding: "8px 4px", fontStyle: "italic" }}>—</div>
          )}
          {(form.passingRecipients || []).map(function (r, idx) {
            return (
              <div key={r.id || idx} style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr auto", gap: 8, marginBottom: 8 }}>
                <input
                  type="text"
                  placeholder={l.contactName}
                  value={r.name || ""}
                  onChange={function (e) {
                    var v = e.target.value;
                    set("passingRecipients", (form.passingRecipients || []).map(function (rr, i) { return i === idx ? Object.assign({}, rr, { name: v }) : rr; }));
                  }}
                  style={Object.assign({}, inp, { fontWeight: 600 })}
                />
                <input
                  type="email"
                  placeholder={l.contactEmail}
                  value={r.email || ""}
                  onChange={function (e) {
                    var v = e.target.value;
                    set("passingRecipients", (form.passingRecipients || []).map(function (rr, i) { return i === idx ? Object.assign({}, rr, { email: v }) : rr; }));
                  }}
                  style={Object.assign({}, inp, { fontFamily: "'JetBrains Mono', monospace" })}
                />
                <button
                  type="button"
                  onClick={function () {
                    set("passingRecipients", (form.passingRecipients || []).filter(function (_, i) { return i !== idx; }));
                  }}
                  style={{ padding: "7px 12px", borderRadius: 6, border: "1px solid rgba(231,76,60,0.3)", background: "rgba(231,76,60,0.06)", color: "var(--color-danger, #DC2626)", cursor: "pointer", fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" }}
                >× {l.removeRecipient}</button>
              </div>
            );
          })}
          <button
            type="button"
            onClick={function () {
              var list = form.passingRecipients || [];
              set("passingRecipients", list.concat([{ id: Date.now() + Math.random(), name: "", email: "" }]));
            }}
            style={{ padding: "8px 14px", borderRadius: 6, border: "1px dashed var(--color-border)", background: "transparent", color: "#2A7A6F", cursor: "pointer", fontSize: 12, fontWeight: 600, width: "100%", marginTop: 4 }}
          >{l.addRecipient}</button>
        </div>

        {/* Email Confidentiality Notice — appended automatically to the
            end of every SweetLeaf email (Pick Ticket, Passing/BOL,
            Factoring, Warehouse), below the signature. Read by
            appendSlDisclosure() in SweetLeafPortal.js — see fix-dashboard.md. */}
        <div style={{ marginBottom: 18 }}>
          <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>{l.confidentialityDisclosure}</label>
          <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 8, fontStyle: "italic" }}>{l.confidentialityDisclosureHelp}</div>
          <textarea
            value={form.confidentialityDisclosure != null ? form.confidentialityDisclosure : ""}
            onChange={function (e) { set("confidentialityDisclosure", e.target.value); }}
            rows={10}
            style={Object.assign({}, inp, { resize: "vertical", fontSize: 12, lineHeight: 1.45 })}
            placeholder={DEFAULT_CONFIDENTIALITY_DISCLOSURE}
          />
        </div>

        {/* Factoring Email Subject — line shown in the recipient's
            inbox; EmailFactoringModal.doSend reads this on send. */}
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>{l.factoringSubject}</label>
          <input
            type="text"
            value={form.factoringEmailSubject || ""}
            onChange={function (e) { set("factoringEmailSubject", e.target.value); }}
            style={Object.assign({}, inp, { fontWeight: 600 })}
            placeholder={DEFAULT_FACTORING_EMAIL_SUBJECT}
          />
          <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 3, fontStyle: "italic" }}>{l.factoringSubjectHelp}</div>
        </div>

        {/* Factoring Email Template — intro text Arturo controls.
            The invoice table + 85% advance row + AGROCAP note are
            generated by EmailFactoringModal.buildBodyHtml below
            this template on send. */}
        <div style={{ marginBottom: 18 }}>
          <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>{l.factoringTemplate}</label>
          <textarea
            value={form.factoringEmailTemplate || ""}
            onChange={function (e) { set("factoringEmailTemplate", e.target.value); }}
            rows={6}
            style={Object.assign({}, inp, { resize: "vertical", fontSize: 12, lineHeight: 1.45 })}
            placeholder={DEFAULT_FACTORING_EMAIL_TEMPLATE}
          />
          <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 3, fontStyle: "italic" }}>{l.factoringTemplateHelp}</div>
        </div>

        {/* Automated Protection Status Reminder settings (2026-09-23) —
            read by netlify/functions/protection-reminder-send.js's daily
            cron. The per-invoice on/off toggle lives on the invoice
            itself (Client Response — Protección box), not here. */}
        <div style={{ marginBottom: 18 }}>
          <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>{l.protectionReminderTitle}</label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div>
              <input
                type="number"
                min="1"
                value={form.protectionReminderIntervalDays != null ? form.protectionReminderIntervalDays : ""}
                onChange={function (e) { set("protectionReminderIntervalDays", parseInt(e.target.value, 10) || 5); }}
                style={Object.assign({}, inp, { fontFamily: "'JetBrains Mono', monospace", fontWeight: 700 })}
              />
              <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 3, fontStyle: "italic" }}>{l.protectionReminderIntervalDaysHelp}</div>
            </div>
            <div>
              <input
                type="number"
                min="1"
                value={form.protectionReminderMaxAuto != null ? form.protectionReminderMaxAuto : ""}
                onChange={function (e) { set("protectionReminderMaxAuto", parseInt(e.target.value, 10) || 3); }}
                style={Object.assign({}, inp, { fontFamily: "'JetBrains Mono', monospace", fontWeight: 700 })}
              />
              <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 3, fontStyle: "italic" }}>{l.protectionReminderMaxAutoHelp}</div>
            </div>
          </div>
          {/* 2026-09-29 (Arturo: "para las alertas de porteccion de
              sweetleaf necesito otra cuenta que se llamara
              Sweetleaf-alerts@gfinexus.com" -> "SL Automated") —
              dedicated, name-free From for ONLY this automated
              cron. Blank = falls back to Masked From above (every
              manual SweetLeaf send's From, unaffected by this
              field). */}
          <div style={{ marginTop: 14 }}>
            <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>{l.protectionReminderMaskedEmailAccount}</label>
            <input
              type="email"
              value={form.protectionReminderMaskedEmailAccount || ""}
              onChange={function (e) { set("protectionReminderMaskedEmailAccount", e.target.value); }}
              style={Object.assign({}, inp, { fontFamily: "'JetBrains Mono', monospace" })}
              placeholder="Sweetleaf-alerts@gfinexus.com"
            />
            <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 3, fontStyle: "italic" }}>{l.protectionReminderMaskedEmailAccountHelp}</div>
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", paddingTop: 12, borderTop: "1px solid var(--color-border)" }}>
          <button
            onClick={doSave}
            disabled={!dirty || saving}
            style={{ padding: "9px 22px", borderRadius: 6, border: "none", background: dirty ? "#2A7A6F" : "var(--color-border)", color: dirty ? "#fff" : "var(--color-text-muted)", cursor: dirty ? "pointer" : "not-allowed", fontSize: 13, fontWeight: 700 }}
          >{l.save}</button>
        </div>
      </div>

      {toast && createPortal(
        <div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", background: "#2A7A6F", color: "#fff", padding: "10px 22px", borderRadius: 10, fontSize: 13, fontWeight: 600, zIndex: 999999, boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}>
          {toast}
        </div>,
        document.body
      )}
    </div>
  );
}
